import type { ILicensePlateRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { LicensePlate, PlateStatus } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiLicensePlateResponse {
  id: string;
  number: string;
  alias: string;
  status?: 'WHITELIST' | 'BLACKLIST';
  notes?: string | null;
}

interface ApiCreateLicensePlateRequest {
  number: string;
  alias: string;
  notes?: string;
}

interface ApiUpdateLicensePlateRequest {
  alias?: string;
  status?: 'WHITELIST' | 'BLACKLIST';
  notes?: string;
}

// Mapea la respuesta de la API al tipo LicensePlate interno
const mapApiLicensePlateToLicensePlate = (apiPlate: ApiLicensePlateResponse, addedAt?: string): LicensePlate => ({
  id: apiPlate.id,
  number: apiPlate.number,
  alias: apiPlate.alias,
  status: (apiPlate.status || 'WHITELIST') as PlateStatus,
  notes: apiPlate.notes || undefined,
  addedAt: addedAt || new Date().toISOString(),
});

export class ApiLicensePlateRepository implements ILicensePlateRepository {
  async getAllPlates(): Promise<LicensePlate[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiLicensePlateResponse>>('v1/access-control/plates');
    return response.data.map((p) => mapApiLicensePlateToLicensePlate(p));
  }

  async getPlates(params?: QueryParams): Promise<PaginatedResponse<LicensePlate>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiLicensePlateResponse>>(
      `v1/access-control/plates?${queryString}`
    );
    return {
      ...response,
      data: response.data.map((p) => mapApiLicensePlateToLicensePlate(p)),
    };
  }

  async createPlate(plate: Omit<LicensePlate, 'id' | 'addedAt'>): Promise<LicensePlate> {
    const client = getApiClient();
    const request: ApiCreateLicensePlateRequest = {
      number: plate.number,
      alias: plate.alias,
      notes: plate.notes,
    };
    const apiPlate = await client.post<ApiLicensePlateResponse>('v1/access-control/plates', request);
    return mapApiLicensePlateToLicensePlate(apiPlate);
  }

  async updatePlate(plate: LicensePlate): Promise<LicensePlate> {
    const client = getApiClient();
    const request: ApiUpdateLicensePlateRequest = {
      alias: plate.alias,
      status: plate.status,
      notes: plate.notes,
    };
    const apiPlate = await client.put<ApiLicensePlateResponse>(`v1/access-control/plates/${plate.id}`, request);
    return mapApiLicensePlateToLicensePlate(apiPlate, plate.addedAt);
  }

  async deletePlate(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/access-control/plates/${id}`);
  }
}

